// src/components/gavion/integrations/M365ApiRoot.tsx
import {
  useDataProvider,
  useGetIdentity,
  usePermissions,
  useTranslate,
} from "ra-core";
import { useQueryClient } from "@tanstack/react-query";
import { useEffect, useMemo, type ReactNode } from "react";
import { getSupabaseClient } from "@/components/atomic-crm/providers/supabase/supabase";
import { dataProviderStore } from "../dialer/api/recordStore";
import { IS_DEMO } from "../env";
import { M365ApiProvider } from "./api/M365ApiContext";
import { createFakeM365Api } from "./api/fakeM365Api";
import {
  createSupabaseM365Api,
  type SupabaseLike,
} from "./api/supabaseM365Api";
import { unavailableM365Api } from "./api/unavailableM365Api";
import { installMicrosoftHandoff } from "./microsoftHandoff";

type HandoffClient = Parameters<typeof installMicrosoftHandoff>[0];
type RealClient = SupabaseLike & HandoffClient;

/**
 * The app's Supabase client. Only under unit tests (no VITE_SUPABASE_URL) does a failure fall back to
 * null (the unavailable API); in a real build a misconfiguration throws instead of being masked.
 */
const isTestEnv = (): boolean =>
  import.meta.env.MODE === "test" || Boolean(import.meta.env.VITEST);
export const safeSupabaseClient = (
  get: () => unknown = getSupabaseClient,
  testEnv: boolean = isTestEnv(),
): RealClient | null => {
  try {
    return get() as RealClient;
  } catch (e) {
    if (testEnv) return null;
    throw e;
  }
};

/** One M365Api for the app (mounted by GavionLayout): the fake in demo, Supabase otherwise. */
export const M365ApiRoot = ({
  children,
  isDemo = IS_DEMO,
  client,
}: {
  children: ReactNode;
  isDemo?: boolean;
  /** Tests only; defaults to getSupabaseClient(). */
  client?: RealClient;
}) => {
  const dataProvider = useDataProvider();
  const translate = useTranslate();
  const { identity } = useGetIdentity();
  const { permissions } = usePermissions();
  const me = identity?.id;
  const signedIn = me !== undefined && me !== null;
  const real = useMemo(
    () => (isDemo ? null : (client ?? safeSupabaseClient())),
    [isDemo, client],
  );
  const api = useMemo(() => {
    if (!signedIn) return unavailableM365Api();
    if (!isDemo)
      return real ? createSupabaseM365Api(real) : unavailableM365Api();
    return createFakeM365Api({
      store: dataProviderStore(dataProvider),
      me: Number(me),
      isAdmin: permissions === "admin",
      translate,
    });
  }, [dataProvider, me, signedIn, isDemo, permissions, translate, real]);
  // The first render often has no identity yet (unavailable API: "not connected"). Every M365 query
  // shares one key prefix, so drop what the previous API answered and refetch with the current one.
  const queryClient = useQueryClient();
  useEffect(() => {
    void queryClient.resetQueries({ queryKey: ["gavion", "m365"] });
  }, [api, queryClient]);
  // GAVION batch 3: the sign-in token handoff (spec 01 §2.1), once a real API exists.
  useEffect(() => {
    if (!real || !signedIn) return;
    return installMicrosoftHandoff(real, (t) => api.storeMicrosoftTokens(t));
  }, [api, real, signedIn]);
  return <M365ApiProvider api={api}>{children}</M365ApiProvider>;
};
